import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent, useCollectionBinding, useContentToken } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

interface Amenity {
  title?: string | null;
  description?: string | null;
  group?: string | null;
  amenity_icon?: string | null;
  amenity_image?: string | null;
  amenity_image_alt_text?: string | null;
  is_featured?: string | null;
  [key: string]: unknown;
}

interface GalleryPhoto {
  image?: string | null;
  title?: string | null;
  description?: string | null;
  alt_text?: string | null;
  [key: string]: unknown;
}

// ============================================================================
// Full-width photo slider
//
// A single image spanning the group's width, auto-advancing with prev/next
// arrows and dot indicators. Mirrors the demo's amenity carousels which sit
// above each amenity list.
// ============================================================================

const SLIDE_INTERVAL = 5000;

interface PhotoSliderProps {
  photos: GalleryPhoto[];
  heading: string;
}

function PhotoSlider({ photos, heading }: PhotoSliderProps) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const count = photos.length;

  const advance = useCallback((dir: 1 | -1) => {
    setCurrent(prev => (prev + dir + count) % count);
  }, [count]);

  useEffect(() => {
    if (count <= 1 || paused) return;
    timerRef.current = setInterval(() => advance(1), SLIDE_INTERVAL);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [count, paused, advance]);

  if (count === 0) return null;

  const safeCurrent = Math.min(current, count - 1);

  return (
    <div
      className="amenities-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {photos.map((photo, i) => (
        <VibesitesImage
          key={photo.image || i}
          url={photo.image!}
          alt={photo.alt_text || photo.title || heading}
          className={`amenities-slider-img${i === safeCurrent ? ' amenities-slider-img--active' : ''}`}
          priority
        />
      ))}

      {count > 1 && (
        <>
          <button
            className="amenities-slider-arrow amenities-slider-arrow--prev"
            onClick={() => advance(-1)}
            aria-label="Previous photo"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <button
            className="amenities-slider-arrow amenities-slider-arrow--next"
            onClick={() => advance(1)}
            aria-label="Next photo"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>

          <div className="amenities-slider-counter" aria-live="polite">
            {safeCurrent + 1} / {count}
          </div>

          <div className="amenities-slider-dots">
            {photos.map((_, i) => (
              <button
                key={i}
                className={`amenities-slider-dot${i === safeCurrent ? ' amenities-slider-dot--active' : ''}`}
                onClick={() => setCurrent(i)}
                aria-label={`Photo ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Amenity group: full-width carousel, heading below it, then a paginated list.
// ============================================================================

const ITEMS_PER_PAGE = 8;

interface AmenityGroupProps {
  headingNode: ReactNode;
  headingText: string;
  items: Amenity[];
  photos: GalleryPhoto[];
}

function AmenityGroup({ headingNode, headingText, items, photos }: AmenityGroupProps) {
  const groupRef = useScrollReveal<HTMLDivElement>();
  const hasImage = photos.length > 0;

  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(items.length / ITEMS_PER_PAGE));
  const safePage = Math.min(page, totalPages - 1);
  const currentItems = items.slice(
    safePage * ITEMS_PER_PAGE,
    (safePage + 1) * ITEMS_PER_PAGE,
  );

  return (
    <div ref={groupRef} className="reveal-fade-up amenities-group">
      {hasImage && (
        <div className="amenities-group-gallery">
          <PhotoSlider photos={photos} heading={headingText} />
        </div>
      )}

      {headingNode}

      <ul className="amenities-col-list">
        {currentItems.map((item, i) => (
          <li key={`${safePage}-${i}`} className="amenities-col-item">
            {item.title || ''}
          </li>
        ))}
      </ul>

      {totalPages > 1 && (
        <div className="amenities-pagination">
          <button
            type="button"
            className="amenities-pagination-btn"
            onClick={() => setPage(Math.max(0, safePage - 1))}
            disabled={safePage === 0}
            aria-label="Previous amenities page"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <span className="amenities-pagination-info">
            {safePage + 1} / {totalPages}
          </span>
          <button
            type="button"
            className="amenities-pagination-btn"
            onClick={() => setPage(Math.min(totalPages - 1, safePage + 1))}
            disabled={safePage === totalPages - 1}
            aria-label="Next amenities page"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

// Strip HTML tags from a richtext value so it can be used as a plain
// string (e.g. as fallback alt text on a photo slider).
function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

export function AmenitiesList() {
  const communityHeadingRaw = useContentToken('amenitieslist.communityHeading');
  const apartmentHeadingRaw = useContentToken('amenitieslist.apartmentHeading');
  const communityHeadingText = stripHtml(communityHeadingRaw) || 'Community Amenities';
  const apartmentHeadingText = stripHtml(apartmentHeadingRaw) || 'Interior Features';

  const communityItems = useCollectionBinding<Amenity>('amenitieslist.communityItems', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });

  const apartmentItems = useCollectionBinding<Amenity>('amenitieslist.apartmentItems', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });

  const communityPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.communityPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Community' },
    defaultLimit: 8,
  });

  const apartmentPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.apartmentPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Apartment' },
    defaultLimit: 8,
  });

  useRevealStyles();

  const hasContent = communityItems.length > 0 || apartmentItems.length > 0;

  return (
    <section className="pt-2 md:pt-4 pb-10 md:pb-16 bg-[var(--vibe-color-background)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {hasContent ? (
          <div className="amenities-stack">
            {communityItems.length > 0 && (
              <AmenityGroup
                headingNode={
                  <RichTextContent
                    path="amenitieslist.communityHeading"
                    label="Community Heading"
                    defaultValue="<p>Community Amenities</p>"
                    as="h3"
                    className="amenities-col-heading"
                  />
                }
                headingText={communityHeadingText}
                items={communityItems}
                photos={communityPhotos}
              />
            )}

            {apartmentItems.length > 0 && (
              <AmenityGroup
                headingNode={
                  <RichTextContent
                    path="amenitieslist.apartmentHeading"
                    label="Apartment Heading"
                    defaultValue="<p>Interior Features</p>"
                    as="h3"
                    className="amenities-col-heading"
                  />
                }
                headingText={apartmentHeadingText}
                items={apartmentItems}
                photos={apartmentPhotos}
              />
            )}
          </div>
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
            <p className="font-body text-lg">Amenities will be displayed here.</p>
          </div>
        )}
      </div>

      <style>{`
        /* Two groups side by side */
        .amenities-stack {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2.5rem;
          align-items: start;
        }
        @media (max-width: 768px) {
          .amenities-stack {
            grid-template-columns: 1fr;
            gap: 3rem;
          }
        }

        .amenities-group {
          display: flex;
          flex-direction: column;
          /* Light gray frame around each gallery + list group */
          border: 1px solid color-mix(in srgb, var(--vibe-color-text) 14%, transparent);
          border-radius: 4px;
          overflow: hidden;
          padding: 0 1.5rem 1.75rem;
        }

        /* Section sub-heading — sits below the gallery. Matches the hero page
           title face (Josefin Slab slab-serif). */
        .amenities-col-heading {
          font-family: "Josefin Slab", Rockwell, Georgia, serif;
          font-size: clamp(1.5rem, 3vw, 2.75rem);
          font-weight: 700;
          letter-spacing: 0.05em;
          color: var(--vibe-color-text);
          text-align: center;
          margin: 0 0 1.25rem;
        }

        /* Full-width carousel sitting above each heading. Negative side margins
           let the image bleed out to the group's light gray border edges while
           the heading and list keep the group's horizontal padding. */
        .amenities-group-gallery {
          width: auto;
          margin: 0 -1.5rem 1.5rem;
        }

        .amenities-slider {
          position: relative;
          width: 100%;
          /* Near-square — slightly wider than tall, matching the example */
          aspect-ratio: 7 / 6;
          overflow: hidden;
          /* No own radius — the group's rounded border clips the top corners. */
          border-radius: 0;
          background: #000;
        }
        .amenities-slider-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .amenities-slider-img--active {
          opacity: 1;
        }

        .amenities-slider-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: none;
          background: rgba(0, 0, 0, 0.4);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.3s, background 0.3s;
          z-index: 2;
        }
        .amenities-slider:hover .amenities-slider-arrow {
          opacity: 1;
        }
        .amenities-slider-arrow:hover {
          background: rgba(0, 0, 0, 0.6);
        }
        .amenities-slider-arrow--prev { left: 14px; }
        .amenities-slider-arrow--next { right: 14px; }

        .amenities-slider-counter {
          position: absolute;
          top: 0.75rem;
          right: 0.9rem;
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          letter-spacing: 0.04em;
          color: #fff;
          background: rgba(0, 0, 0, 0.45);
          padding: 0.15rem 0.6rem;
          border-radius: 999px;
          z-index: 2;
          pointer-events: none;
        }

        .amenities-slider-dots {
          position: absolute;
          bottom: 14px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 7px;
          z-index: 2;
          max-width: calc(100% - 6rem);
          flex-wrap: wrap;
          justify-content: center;
        }
        .amenities-slider-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          border: 1.5px solid rgba(255, 255, 255, 0.7);
          background: transparent;
          padding: 0;
          cursor: pointer;
          transition: background 0.25s, border-color 0.25s, transform 0.25s;
        }
        .amenities-slider-dot:hover {
          transform: scale(1.25);
          border-color: white;
        }
        .amenities-slider-dot--active {
          background: white;
          border-color: white;
        }

        @media (prefers-reduced-motion: reduce) {
          .amenities-slider-img {
            transition: none;
          }
        }

        /* Centered single-column list flowing below the carousel */
        .amenities-col-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .amenities-col-item {
          padding: 0.45rem 0;
          text-align: center;
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          color: var(--vibe-color-text);
          line-height: 1.5;
        }
        @media (max-width: 900px) {
          .amenities-col-item { font-size: 0.9rem; }
        }

        /* Pagination controls — only rendered when a list spans multiple pages */
        .amenities-pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.7rem;
          margin-top: 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          color: var(--vibe-color-text);
        }
        .amenities-pagination-btn {
          width: 30px;
          height: 30px;
          padding: 0;
          border-radius: 50%;
          border: 1px solid color-mix(in srgb, var(--vibe-color-text) 18%, transparent);
          background: white;
          color: var(--vibe-color-text);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.2s, border-color 0.2s, color 0.2s, opacity 0.2s;
        }
        .amenities-pagination-btn:hover:not(:disabled) {
          background: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
        }
        .amenities-pagination-btn:disabled {
          opacity: 0.35;
          cursor: not-allowed;
        }
        .amenities-pagination-info {
          min-width: 3rem;
          text-align: center;
          font-variant-numeric: tabular-nums;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesList',
  displayName: 'Amenities List',
  tokens: {
    'amenitieslist.communityHeading': {
      label: 'Community Heading',
      type: 'richtext',
      defaultValue: '<p>Community Amenities</p>',
    },
    'amenitieslist.apartmentHeading': {
      label: 'Apartment Heading',
      type: 'richtext',
      defaultValue: '<p>Interior Features</p>',
    },
  },
  collectionBindings: {
    'amenitieslist.communityItems': {
      label: 'Community Amenities',
      collection: 'amenities',
    },
    'amenitieslist.apartmentItems': {
      label: 'Apartment Features',
      collection: 'amenities',
    },
    'amenitieslist.communityPhotos': {
      label: 'Community Photos',
      collection: 'photo_gallery',
    },
    'amenitieslist.apartmentPhotos': {
      label: 'Apartment Photos',
      collection: 'photo_gallery',
    },
  },
};

(AmenitiesList as any).contentSchema = contentSchema;

export default AmenitiesList;
